import React, {useState, useEffect, useContext} from 'react';
import {Block} from './block';
import {isUndefined} from 'lodash';
import {MarkdownEditor} from './markdown-editor';
import {AppContext} from '../state';
import MarkdownIt from 'markdown-it';
import classnames from 'classnames';
import trim from 'trim';

import {Typography} from 'antd';

export const markdown = MarkdownIt();

// Remember old renderer, if overridden, or proxy to default renderer
const defaultRender =
    markdown.renderer.rules.link_open ||
    function (tokens, idx, options, env, self) {
        return self.renderToken(tokens, idx, options);
    };
const defaultImageRenderer = markdown.renderer.rules.image;

markdown.renderer.rules.link_open = function (tokens, idx, options, env, self) {
    // If you are sure other plugins can't add `target` - drop check below
    var aIndex = tokens[idx].attrIndex('target');

    if (aIndex < 0) {
        tokens[idx].attrPush(['target', '_blank']); // add new attribute
    } else {
        tokens[idx].attrs[aIndex][1] = '_blank'; // replace value of existing attr
    }

    // pass token to default renderer.
    return defaultRender(tokens, idx, options, env, self);
};

markdown.renderer.rules.image = function (tokens, idx, options, env, slf) {
    try {
        const src = tokens[idx].attrs.find((a) => a[0] === 'src');
        const classes = tokens[idx].content.split(',').map(trim);
        switch (classes.length) {
            case 0:
                return `<img src="${src[1]}" class="md-image-centered" />`;
            case 1:
                return `<img src="${src[1]}" class="md-image-${classes[0]}" />`;
            case 2:
                return `<img src="${src[1]}" class="md-image-${classes[0]}" style="width: ${classes[1]}"/>`;
            case 3:
            default:
                return `<img src="${src[1]}" class="md-image-${classes[0]}" style="width: ${classes[1]}; padding: ${classes[2]}px"/>`;
        }
    } catch (err) {
        console.error('image rendering error', err, tokens[idx]);
        return null;
    }
};

export function MarkdownBlock({source, className, onSave, onCancel, size = 300, onValue, onChange, placeholder, delegatedEditMode, triggerEdit, imageRootPath}) {
    const {backend} = useContext(AppContext);
    const [editMode, setEditMode] = useState();
    const [value, setValue] = useState('');

    const handleSave = () => {
        setEditMode(false);
        onSave && onSave(value);
    };
    const handleCancel = () => {
        setEditMode(false);
        onCancel && onCancel();
    };

    const handleValue = (val) => {
        setValue(val);
        onValue && onValue(val);
        onChange && onChange(val);
    };

    useEffect(() => {
        if (source) {
            setValue(source);
        }
    }, [source]);

    useEffect(() => {
        if (!isUndefined(delegatedEditMode)) {
            setEditMode(delegatedEditMode);
        }
    }, [delegatedEditMode]);

    function onEdit() {
        if (!delegatedEditMode) {
            setEditMode(true);
        }
    }

    async function handleImageUpload(file) {
        console.log('uploading image file', file, imageRootPath);
        const [workgroup, key] = imageRootPath.split('/');
        const data = await backend.uploadImage(file, workgroup, key);
        return data;
    }

    markdown.normalizeLink = function (url) {
        // Leave non-URL strings alone. markdown-it calls this for every
        // link or image in the rendered markdown; a spec's description may
        // contain relative paths ("/foo"), anchors ("#bar"), or plain text
        // that doesn't parse as a URL. Without this guard, new URL()
        // throws "Invalid URL" and takes the whole render tree down.
        if (!backend || !backend.accessToken || !url) return url;
        try {
            const imgUrl = new URL(url);
            imgUrl.search = `?token=${backend.accessToken.sessionId}`;
            return imgUrl.href;
        } catch {
            return url;
        }
    };

    return (
        <Block name={placeholder} className={className} onSave={handleSave} onCancel={handleCancel} onEdit={onEdit} delegatedEditMode={delegatedEditMode}>
            {!editMode && source && <div className="textValue" onClick={() => triggerEdit && triggerEdit()} dangerouslySetInnerHTML={{__html: markdown.render(source)}} />}
            {editMode && <MarkdownEditor value={source} height={size} onUpload={handleImageUpload} onValue={handleValue} onChange={handleValue} />}
        </Block>
    );
}

export function MarkdownView({source, className}) {
    const classes = classnames('textValue', className);
    const {backend} = useContext(AppContext);

    markdown.normalizeLink = function (url) {
        // Leave non-URL strings alone. markdown-it calls this for every
        // link or image in the rendered markdown; a spec's description may
        // contain relative paths ("/foo"), anchors ("#bar"), or plain text
        // that doesn't parse as a URL. Without this guard, new URL()
        // throws "Invalid URL" and takes the whole render tree down.
        if (!backend || !backend.accessToken || !url) return url;
        try {
            const imgUrl = new URL(url);
            imgUrl.search = `?token=${backend.accessToken.sessionId}`;
            return imgUrl.href;
        } catch {
            return url;
        }
    };

    return source ? (
        <Typography.Paragraph>
            <span className={classes} dangerouslySetInnerHTML={{__html: markdown.render(source)}} />
        </Typography.Paragraph>
    ) : null;
}
